/**
 * Modelo da lista que monta a Home do app (`appHomeItems`, servido no `app-info`).
 *
 * Duas naturezas convivem na lista ordenada:
 *
 * - `section` é fixa: existe porque o build do app tem aquele componente. Move, oculta
 *   e renomeia, não se adiciona nem se remove.
 * - `row` é uma LINHA criada pelo operador, e é ela que carrega os blocos de imagem.
 *
 * A linha é quem define a largura, não o bloco: `layout: 'full'` cabe um bloco e ocupa
 * a tela toda; `layout: 'split'` cabe dois, lado a lado. Uma `split` com um bloco só
 * tem vaga livre — e o admin mostra essa vaga, que é como o operador enxerga que a
 * linha ficou pela metade.
 */

import { ROLETA_RESOURCE } from '../../../models/roleta';

export type HomeItemKind = 'section' | 'row';
export type RowLayout = 'full' | 'split';

export interface HomeNavigation {
  method: 'screen' | 'webview' | 'whatsapp';
  destination: string | null;
  whatsappMessage?: string | null;
  params?: unknown;
}

/** Bloco de imagem. Vive sempre dentro de uma `row`. */
export interface HomeBlock {
  id: string;
  imagePath?: string | null;
  navigation?: HomeNavigation | null;
  /** Etiquetas de cliente que veem o bloco. Vazio = todo mundo. */
  tagIds?: string[] | null;
  /**
   * Segmentação por grupo, do desenho original. O editor não oferece mais, mas o campo
   * fica no tipo para atravessar o save de bloco configurado antes da troca em vez de
   * ser descartado em silêncio. Quem segmenta por grupo hoje é o carrossel.
   */
  groupIds?: string[] | null;
  /** Início da janela de exibição, ISO em UTC. Vazio = já vale. */
  startAt?: string | null;
  /** Fim da janela, ISO em UTC. Vazio = não vence. */
  endAt?: string | null;
}

/**
 * `datetime-local` → ISO em UTC. O input devolve a hora local SEM fuso
 * (`2026-10-21T15:30`), e o backend roda em UTC: sem converter, 15:30 de Brasília viraria
 * 15:30Z e o bloco venceria 3h antes. Mesmo motivo do `DateTimeInputUTC`.
 */
export const dateInputToIso = (value: string): string | null => {
  if (!value) { return null; }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date.toISOString();
};

/** ISO → `datetime-local`, de volta na hora local de quem está olhando. */
export const isoToDateInput = (iso?: string | null): string => {
  if (!iso) { return ''; }
  const date = new Date(iso);
  if (isNaN(date.getTime())) { return ''; }

  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * Card do carrossel (`firebaseConfig.home_cards`). Mesmo contrato do bloco, sem `id`: o
 * validator do backend recusa chave desconhecida, e a ordem é a posição na lista.
 */
export type CarouselCard = Omit<HomeBlock, 'id'> & { index?: number };

export type ShowWindowStatus = 'live' | 'scheduled' | 'ended';

const parseTime = (iso?: string | null): number | null => {
  if (!iso) { return null; }
  const time = new Date(iso).getTime();
  return isNaN(time) ? null : time;
};

/** Mesma regra do `isCardWithinWindow` do app: data ausente ou ilegível não restringe. */
export const showWindowStatus = (
  item: Pick<HomeBlock, 'startAt' | 'endAt'>,
  now: number = Date.now(),
): ShowWindowStatus => {
  const startAt = parseTime(item.startAt);
  if (startAt !== null && now < startAt) { return 'scheduled'; }
  const endAt = parseTime(item.endAt);
  if (endAt !== null && now > endAt) { return 'ended'; }
  return 'live';
};

export interface HomeItem {
  kind: HomeItemKind;
  id: string;
  visible: boolean;
  layout?: RowLayout;
  /**
   * Na linha, título acima dela no app — vazio = a linha não abre espaço. Na seção,
   * substitui o `defaultTitle` do catálogo — vazio = o app mostra o texto do build.
   */
  title?: string | null;
  blocks?: HomeBlock[];
}

export interface HomeSectionDef {
  id: string;
  label: string;
  /**
   * Título que o build mostra na seção. Quem não tem não mostra título nenhum (card,
   * carrossel), e por isso não ganha o campo de renomear.
   */
  defaultTitle?: string;
}

/**
 * Fallback enquanto o backend não devolve `availableHomeSections` no GET de
 * chains-config. A fonte da verdade é o build do app (`SCREENS` em pages/Home);
 * quando o endpoint existir, o que vier dele vence — ver `resolveSectionCatalog`.
 */
export const FALLBACK_HOME_SECTIONS: HomeSectionDef[] = [
  { id: 'payments', label: 'Pagamentos pendentes' },
  { id: 'categories', label: 'Categorias', defaultTitle: 'Categorias' },
  { id: 'roleta', label: 'Roleta da Sorte' },
  { id: 'carousel', label: 'Carrossel de banners' },
  { id: 'redeemed', label: 'Últimos produtos resgatados', defaultTitle: 'Últimos produtos resgatados' },
  { id: 'visited', label: 'Últimos locais visitados', defaultTitle: 'Últimos locais visitados' },
  { id: 'lastFillins', label: 'Últimos abastecimentos', defaultTitle: 'Últimos Abastecimentos' },
  { id: 'contact', label: 'Contato', defaultTitle: 'Entre em contato' },
];

/** Seção cujo conteúdo é editado em outro lugar da tela. */
export const SECTION_CONTENT_HINT: Record<string, string> = {
  carousel: 'As imagens ficam em "Imagens do carrossel", logo abaixo.',
};

export const ROW_LAYOUTS: RowLayout[] = ['full', 'split'];

export const ROW_LAYOUT_LABEL: Record<RowLayout, string> = {
  full: 'Uma imagem',
  split: 'Duas lado a lado',
};

export const ROW_LAYOUT_DESCRIPTION: Record<RowLayout, string> = {
  full: 'Ocupa a largura toda da tela.',
  split: 'Duas imagens dividindo a mesma linha.',
};

export const ROW_CAPACITY: Record<RowLayout, number> = { full: 1, split: 2 };

/** Limite do `title`, tanto da linha quanto do título renomeado de uma seção. */
export const ROW_TITLE_MAX = 60;

/**
 * Dimensão alvo por layout. O card é imagem pura com o texto chapado dentro: a arte da
 * linha inteira reaproveitada na metade fica ilegível, então cada uma tem a sua.
 */
export const ROW_ART: Record<RowLayout, { ratio: number; width: number; height: number }> = {
  full: { ratio: 11 / 5, width: 1100, height: 500 },
  split: { ratio: 5 / 3, width: 520, height: 312 },
};

/**
 * Tipos de bloco que o operador pode adicionar. Hoje só imagem; a lista existe para o
 * "+ Adicionar bloco" crescer sem mexer na tela.
 */
export const BLOCK_TYPES = [
  {
    kind: 'row' as const,
    label: 'Bloco de imagem',
    description: 'Uma arte clicável, sozinha na linha ou dividindo com outra.',
  },
];

export const newId = () =>
  globalThis.crypto?.randomUUID?.() ?? `b-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const makeBlock = (): HomeBlock => ({
  id: newId(),
  imagePath: null,
  navigation: { method: 'screen', destination: '' },
  tagIds: [],
});

export const makeRow = (layout: RowLayout): HomeItem => ({
  kind: 'row',
  id: newId(),
  visible: true,
  layout,
  title: '',
  blocks: [makeBlock()],
});

export const rowCapacity = (row: HomeItem) => ROW_CAPACITY[row.layout || 'full'];
export const rowIsFull = (row: HomeItem) => (row.blocks?.length || 0) >= rowCapacity(row);

/**
 * Normaliza o que vem salvo. Cobre o formato antigo (bloco solto com `span`, de antes
 * de a linha existir), para que uma rede configurada na versão anterior não perca nada:
 * cada bloco vira uma linha, e dois `half` que estavam vizinhos voltam juntos.
 */
const adoptLegacy = (saved: any[]): any[] => {
  const out: any[] = [];
  let i = 0;
  while (i < saved.length) {
    const item = saved[i];
    if (item?.kind !== 'banner') { out.push(item); i += 1; continue; }

    const next = saved[i + 1];
    const pairs = item.span === 'half' && next?.kind === 'banner' && next.span === 'half';
    const toBlock = (b: any): HomeBlock => ({
      id: b.id,
      imagePath: b.imagePath ?? null,
      navigation: b.navigation ?? null,
      tagIds: b.tagIds ?? [],
      groupIds: b.groupIds ?? undefined,
    });

    out.push({
      kind: 'row',
      id: item.id,
      visible: item.visible !== false,
      layout: item.span === 'half' ? 'split' : 'full',
      blocks: pairs ? [toBlock(item), toBlock(next)] : [toBlock(item)],
    });
    i += pairs ? 2 : 1;
  }
  return out;
};

export const resolveSectionCatalog = (fromServer?: HomeSectionDef[] | null): HomeSectionDef[] =>
  (Array.isArray(fromServer) && fromServer.length) ? fromServer : FALLBACK_HOME_SECTIONS;

/**
 * Junta o que está salvo com o catálogo de seções. Seção que o catálogo tem e o salvo
 * não menciona entra no FIM, visível — é o que faz uma seção recém-lançada aparecer
 * para quem já configurou, em vez de sumir em silêncio. Mesma regra que o app aplica.
 *
 * Id de seção que o catálogo não conhece é descartado (seção removida do app).
 */
export const mergeWithCatalog = (saved: HomeItem[] | undefined, catalog: HomeSectionDef[]): HomeItem[] => {
  const known = new Map(catalog.map(section => [section.id, section]));
  const seen = new Set<string>();
  const merged: HomeItem[] = [];

  adoptLegacy(Array.isArray(saved) ? saved : []).forEach((item: any) => {
    if (item?.kind === 'row') {
      merged.push({
        kind: 'row',
        id: item.id,
        visible: item.visible !== false,
        layout: item.layout === 'split' ? 'split' : 'full',
        title: item.title || '',
        blocks: Array.isArray(item.blocks) ? item.blocks : [],
      });
      return;
    }
    if (!item?.id || !known.has(item.id) || seen.has(item.id)) { return; }
    seen.add(item.id);
    // O título salvo atravessa o merge intacto, mesmo que o catálogo desta sessão
    // diga que a seção não é renomeável: quem decide se dá para editar é o lápis
    // (que olha o `defaultTitle`). Zerar aqui apagaria texto do operador no save
    // seguinte só porque o catálogo veio de um backend mais antigo.
    merged.push({
      kind: 'section',
      id: item.id,
      visible: item.visible !== false,
      title: item.title,
    });
  });

  catalog.forEach(section => {
    if (!seen.has(section.id)) {
      merged.push({ kind: 'section', id: section.id, visible: true });
    }
  });

  return merged;
};

/**
 * Catálogo das telas do app que um bloco pode abrir.
 *
 * Duas coisas moram aqui porque o seletor é um só e precisa das duas:
 *
 * - `requires` é a flag da rede que libera a tela. Sem o módulo, o destino some do
 *   seletor: a tela CONTINUA registrada no navegador do app (o build é o mesmo para
 *   as 246 redes), então apontar para ela não quebra — abre vazia, que é pior, porque
 *   o operador só descobre pelo cliente reclamando.
 * - `screen` é uma tela ANINHADA, que `navigate` não acha pelo nome puro: o bloco
 *   abre o navegador de fora (`id`) passando `params: { screen }`. O validator do
 *   backend e o `openCardDestination` do app já aceitam `params` — nada muda lá.
 *
 * O nome de cada destino é o texto que o cliente vê no app (menu do Perfil / título
 * da tela), não o nome da rota: é assim que o operador procura.
 */

/** Campo da rede que libera um destino. Vazio/false esconde. */
export type ChainModuleFlag =
  | 'useGift' | 'useBolao' | 'useRoleta' | 'useDrawing' | 'useLuckyNumber'
  | 'usePromoter' | 'useDotz' | 'useVehicleOdometer' | 'referralValue' | 'regulation';

export type ChainModules = Partial<Record<ChainModuleFlag, unknown>>;

/**
 * Item específico que o destino pode abrir direto, em vez da lista.
 *
 * A rota do detalhe é OUTRA, e não a da lista com um param a mais: `RoletaDetail`
 * sem id não mostra lista nenhuma — ele busca a primeira roleta com giros. Por isso
 * id vazio tem de voltar para o `id`/`screen` da escolha, nunca gravar `param: null`.
 *
 * `fieldName`/`emptyName` e não `label`/`emptyText`: o plugin de i18n embrulha essas
 * chaves dentro de objeto no escopo do módulo e congelaria o texto no idioma do
 * primeiro import. A tradução acontece na renderização, com `__t()`.
 */
export interface NavigationTarget {
  /** Rota do detalhe. */
  destination: string;
  /** Tela do detalhe, quando ela não é rota de topo; vira `params.screen`. */
  screen?: string;
  /** Nome do param que carrega o id do item. */
  param: string;
  /** Resource do admin que lista os itens para o seletor. */
  resource: string;
  /** Ordenação — a mesma da lista daquele resource, que o backend já aceita. */
  sort: { field: string; order: 'ASC' | 'DESC' };
  /** Rótulo do seletor do item. */
  fieldName: string;
  /** Opção vazia, que mantém o destino na lista. */
  emptyName: string;
}

export interface NavigationChoice {
  /** Rota de topo, alcançável a partir da Home. */
  id: string;
  name: string;
  /** Tela dentro da rota de topo; vira `params.screen`. */
  screen?: string;
  requires?: ChainModuleFlag;
  /** Quando existe, o seletor oferece um segundo campo para abrir um item direto. */
  target?: NavigationTarget;
}

export interface NavigationGroup {
  /**
   * `name`, e não `label`: o plugin de i18n embrulha `label:` dentro de objeto no
   * escopo do módulo, o que congelaria o subcabeçalho no idioma do primeiro import.
   * A tradução acontece na renderização, com `__t()`.
   */
  name: string;
  choices: NavigationChoice[];
}

export const NAVIGATION_GROUPS: NavigationGroup[] = [
  {
    name: 'Cupons e descontos',
    choices: [
      { id: 'HubCoupon', name: 'Cupons e ofertas' },
      { id: 'Coupons', name: 'Meus cupons' },
      { id: 'ProfileNavigator', screen: 'Discounts', name: 'Meus descontos' },
      { id: 'DotzDiscounts', name: 'Descontos Dotz', requires: 'useDotz' },
    ],
  },
  {
    name: 'Resgates e brindes',
    choices: [
      { id: 'PlacesSelect', name: 'Resgatar produtos' },
      { id: 'ProfileNavigator', screen: 'AvailableGifts', name: 'Clube de vantagens', requires: 'useGift' },
      { id: 'ProfileNavigator', screen: 'MyAchievements', name: 'Minhas conquistas', requires: 'useGift' },
    ],
  },
  {
    name: 'Promoções e sorteios',
    choices: [
      {
        id: 'Roletas',
        name: 'Roleta da Sorte',
        requires: 'useRoleta',
        // A lista já abre a roleta direto quando a rede só tem uma (`navigation.replace`
        // em `Roletas/index.tsx`), então o campo só muda algo com 2+ simultâneas.
        target: {
          destination: 'RoletaDetail',
          param: 'id',
          resource: ROLETA_RESOURCE,
          sort: { field: 'startAt', order: 'DESC' },
          fieldName: 'Roleta',
          emptyName: 'Todas — abre a lista de roletas',
        },
      },
      {
        id: 'Drawings',
        name: 'Sorteios',
        requires: 'useDrawing',
        target: {
          destination: 'Drawings',
          screen: 'DrawingLuckyNumberView',
          param: 'drawingId',
          resource: 'chains/chainId/drawings',
          sort: { field: 'createdAt', order: 'DESC' },
          fieldName: 'Sorteio',
          emptyName: 'Todos — abre a lista de sorteios',
        },
      },
      { id: 'ProfileNavigator', screen: 'LuckyNumber', name: 'Número da sorte', requires: 'useLuckyNumber' },
      {
        id: 'Boloes',
        name: 'Bolões',
        requires: 'useBolao',
        target: {
          destination: 'BolaoDetail',
          param: 'id',
          resource: 'boloes',
          sort: { field: 'scheduledAt', order: 'DESC' },
          fieldName: 'Bolão',
          emptyName: 'Todos — abre a lista de bolões',
        },
      },
    ],
  },
  {
    name: 'Indicação e divulgação',
    choices: [
      { id: 'ReferralCode', name: 'Indique e ganhe', requires: 'referralValue' },
      { id: 'ProfileNavigator', screen: 'PromoterCode', name: 'Promoter', requires: 'usePromoter' },
      // A tela pede um código de campanha, que é uma regra do Clube de Vantagens.
      // A flag exata (`hasActiveUniqueCodeRule`) é calculada no app-info e não vem
      // no cadastro da rede; `useGift` é o módulo que permite criar a campanha.
      { id: 'UniqueCodeRedeem', name: 'Resgatar código de divulgação', requires: 'useGift' },
    ],
  },
  {
    name: 'Abastecimento',
    choices: [
      { id: 'FillIn', name: 'Abastecer' },
      {
        id: 'GasStations',
        name: 'Postos',
        target: {
          destination: 'Station',
          param: 'stationId',
          resource: 'stations',
          sort: { field: 'name', order: 'ASC' },
          fieldName: 'Posto',
          emptyName: 'Todos — abre a lista de postos',
        },
      },
      { id: 'FillinsData', name: 'Histórico de abastecimentos' },
      { id: 'ProfileNavigator', screen: 'FavoriteStation', name: 'Posto favorito' },
    ],
  },
  {
    name: 'Conta e pontos',
    choices: [
      { id: 'History', name: 'Extrato' },
      { id: 'AccountManagement', name: 'Detalhes da conta' },
      { id: 'Notifications', name: 'Notificações' },
      { id: 'ProfileNavigator', name: 'Perfil' },
      { id: 'ProfileNavigator', screen: 'MyVehicles', name: 'Meus veículos', requires: 'useVehicleOdometer' },
    ],
  },
  {
    name: 'Regras',
    choices: [
      { id: 'ProfileNavigator', screen: 'Regulation', name: 'Regulamento', requires: 'regulation' },
      { id: 'Term', name: 'Termos de uso' },
    ],
  },
];

export const NAVIGATION_NONE_LABEL = 'Nenhum — imagem não clicável';

/** Valor do seletor. Destino aninhado vira `rota/tela` numa chave só. */
export const navigationChoiceKey = (choice: Pick<NavigationChoice, 'id' | 'screen'>): string =>
  (choice.screen ? `${choice.id}/${choice.screen}` : choice.id);

const ALL_CHOICES = NAVIGATION_GROUPS.flatMap(group => group.choices);

/** Params que pertencem a um alvo — limpos ao trocar de destino. */
const TARGET_PARAMS = new Set(
  ALL_CHOICES.map(choice => choice.target?.param).filter(Boolean) as string[],
);

export const findNavigationChoice = (key: string): NavigationChoice | undefined =>
  ALL_CHOICES.find(choice => navigationChoiceKey(choice) === key);

export const navigationTargetOf = (key: string): NavigationTarget | undefined =>
  findNavigationChoice(key)?.target;

/** Escolha cujo ALVO é esta rota — é como uma rota de detalhe volta para a lista. */
const choiceByTargetRoute = (destination: string, screen?: string): NavigationChoice | undefined =>
  ALL_CHOICES.find(choice => choice.target
    && choice.target.destination === destination
    && (choice.target.screen || undefined) === (screen || undefined));

/**
 * Chave do seletor a partir do que está salvo no bloco.
 *
 * Bloco apontado para um item específico guarda a rota do DETALHE (`RoletaDetail`),
 * que não é escolha do catálogo: sem este desvio o seletor abriria em branco e o
 * operador perderia o destino ao salvar de novo.
 */
export const navigationKeyOf = (navigation: HomeNavigation | null | undefined): string => {
  const destination = navigation?.destination || '';
  if (!destination) { return ''; }
  const screen = (navigation?.params as { screen?: string } | undefined)?.screen;
  const fromTarget = choiceByTargetRoute(destination, screen);
  if (fromTarget) { return navigationChoiceKey(fromTarget); }
  return screen ? `${destination}/${screen}` : destination;
};

/** Id do item específico salvo no bloco; vazio quando o destino é a lista. */
export const navigationTargetIdOf = (navigation: HomeNavigation | null | undefined): string => {
  const destination = navigation?.destination || '';
  const params = (navigation?.params || {}) as { screen?: string; params?: Record<string, unknown> };
  const target = choiceByTargetRoute(destination, params.screen)?.target;
  if (!target) { return ''; }
  const bag = (target.screen ? params.params : params) as Record<string, unknown> | undefined;
  const id = bag?.[target.param];
  return typeof id === 'string' ? id : '';
};

/** Devolve o `navigation` com destino e params ajustados à chave escolhida. */
export const applyNavigationKey = (navigation: HomeNavigation, key: string): HomeNavigation => {
  const [destination, screen] = key.split('/');
  const previous = (navigation.params || {}) as Record<string, unknown>;
  const params: Record<string, unknown> = {};
  Object.keys(previous).forEach(name => {
    if (name !== 'screen' && name !== 'params' && !TARGET_PARAMS.has(name)) {
      params[name] = previous[name];
    }
  });
  if (screen) { params.screen = screen; }
  return {
    ...navigation,
    destination: destination || '',
    params: Object.keys(params).length ? params : undefined,
  };
};

/**
 * Aplica a escolha do seletor junto com o item específico.
 *
 * Id vazio NÃO vira param nulo na rota de detalhe: volta para a rota da lista, que é
 * a escolha do catálogo. `RoletaDetail` sem id abre a primeira roleta com giros, e
 * `DrawingLuckyNumberView` sem `drawingId` busca `undefined` — nenhum dos dois é
 * "mostrar a lista".
 */
export const applyNavigationTarget = (
  navigation: HomeNavigation,
  key: string,
  id: string,
): HomeNavigation => {
  const target = navigationTargetOf(key);
  if (!target || !id) { return applyNavigationKey(navigation, key); }
  const params = target.screen
    ? { screen: target.screen, params: { [target.param]: id } }
    : { [target.param]: id };
  return { ...navigation, destination: target.destination, params };
};

/**
 * Grupos visíveis para esta rede.
 *
 * `chain` ausente (ainda carregando, ou o GET falhou) não filtra nada: seletor vazio
 * seria pior que seletor largo. E `keepKey` mantém à vista o destino JÁ SALVO mesmo
 * com o módulo desligado — senão o seletor aparece em branco e o operador não tem
 * como enxergar que o banner aponta para uma tela que a rede não tem mais.
 */
export const buildNavigationGroups = (
  chain?: ChainModules | null,
  keepKey?: string,
): NavigationGroup[] => NAVIGATION_GROUPS
  .map(group => ({
    ...group,
    choices: group.choices.filter(choice => (
      !choice.requires
      || !chain
      || !!chain[choice.requires]
      || navigationChoiceKey(choice) === keepKey
    )),
  }))
  .filter(group => group.choices.length > 0);
